import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users2, UserPlus, Pencil, Trash2, UserCheck, Check, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import DynamicFields from "@/components/DynamicFields";
import { useAuth } from "@/context/AuthContext";
import { ALL_ROLES, ROLE_LABELS } from "@/lib/constants";
import api from "@/lib/apiClient";

const PARTNER_ROLES = ALL_ROLES.filter((r) => r !== "customer");
const emptyForm = { name: "", email: "", role: "dealer", phone: "", company: "", city: "", pincode: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [custom, setCustom] = useState({});
  const [edit, setEdit] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/users")).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required."); return; }
    try {
      await api.post("/users", { ...form, custom });
      toast.success("User registered.");
      setOpen(false);
      setForm(emptyForm);
      setCustom({});
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const changeRole = async (uid, role) => {
    try { await api.put(`/users/${uid}`, { role }); toast.success("Role updated."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/users/${edit.user_id}`, {
        name: edit.name, phone: edit.phone, company: edit.company, city: edit.city, pincode: edit.pincode,
      });
      toast.success("User updated.");
      setEdit(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const del = async (uid) => {
    try { await api.delete(`/users/${uid}`); toast.success("User deleted."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const [approveRole, setApproveRole] = useState({});
  const pending = rows.filter((r) => r.status === "pending");

  const approve = async (u) => {
    const role = approveRole[u.user_id] || u.requested_role || "customer";
    try { await api.post(`/users/${u.user_id}/approve`, { role }); toast.success(`${u.name} approved as ${ROLE_LABELS[role]}.`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const reject = async (u) => {
    try { await api.post(`/users/${u.user_id}/reject`); toast.success("Sign-up rejected."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Users & Partners</h1>
          <p className="text-muted-foreground mt-1">Register and manage dealers, distributors, agents, service providers and team.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-user-button"><UserPlus className="h-4 w-4" /> Register User</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="add-user-dialog">
            <DialogHeader><DialogTitle className="font-display">Register New User / Partner</DialogTitle>
              <DialogDescription>Create an account and assign a role.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>Name *</Label><Input value={form.name} onChange={set("name")} data-testid="user-name-input" /></div>
              <div className="space-y-1.5 col-span-2"><Label>Email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="user-email-input" /></div>
              <div className="space-y-1.5 col-span-2">
                <Label>Role *</Label>
                <Select value={form.role} onValueChange={set("role")}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTNER_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
              <div className="space-y-1.5"><Label>Company</Label><Input value={form.company} onChange={set("company")} /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={set("city")} /></div>
              <div className="space-y-1.5"><Label>Pincode</Label><Input value={form.pincode} onChange={set("pincode")} /></div>
              <div className="col-span-2"><DynamicFields entity="user" values={custom} onChange={(k, v) => setCustom((c) => ({ ...c, [k]: v }))} /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="confirm-user-button">Register</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {pending.length > 0 && (
        <Card className="p-5 border-amber-300 bg-amber-50/60" data-testid="pending-approvals">
          <div className="flex items-center gap-2 mb-3">
            <UserCheck className="h-5 w-5 text-amber-700" />
            <h3 className="font-display font-bold">Pending Approvals ({pending.length})</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">New self sign-ups can log in but can't access the portal until you approve them.</p>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.user_id} className="flex flex-col md:flex-row md:items-center gap-3 rounded-lg border bg-card p-3" data-testid={`pending-row-${u.email || u.user_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email || u.phone}</p>
                </div>
                <p className="text-xs text-muted-foreground shrink-0">Requested: <span className="font-semibold text-foreground">{ROLE_LABELS[u.requested_role] || "Customer"}</span></p>
                <Select value={approveRole[u.user_id] || u.requested_role || "customer"} onValueChange={(v) => setApproveRole((m) => ({ ...m, [u.user_id]: v }))}>
                  <SelectTrigger className="w-44 h-9 text-xs" data-testid={`approve-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ALL_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" className="gap-1.5" onClick={() => approve(u)} data-testid={`approve-user-${u.user_id}`}><Check className="h-4 w-4" /> Approve</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="gap-1.5 text-red-600 border-red-200" data-testid={`reject-user-${u.user_id}`}><X className="h-4 w-4" /> Reject</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Reject {u.name}?</AlertDialogTitle>
                        <AlertDialogDescription>The account will be marked rejected. The user can still log in but will only see a rejection notice.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => reject(u)} className="bg-red-600 hover:bg-red-700">Reject</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>User</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground"><Users2 className="h-10 w-10 mx-auto mb-2 opacity-40" />No users.</TableCell></TableRow>
            ) : rows.map((u) => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.email}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9"><AvatarImage src={u.picture} /><AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">{u.name?.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                    <div><p className="font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
                    {u.status === "pending" && <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" data-testid={`status-pending-${u.email}`}>Pending</span>}
                    {u.status === "rejected" && <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-red-100 text-red-700 px-2 py-0.5" data-testid={`status-rejected-${u.email}`}>Rejected</span>}
                  </div>
                </TableCell>
                <TableCell className="text-sm">{u.phone || "—"}<div className="text-xs text-muted-foreground">{[u.city, u.pincode].filter(Boolean).join(", ")}</div></TableCell>
                <TableCell className="text-sm">{u.company || "—"}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                    <SelectTrigger className="w-40 h-8 text-xs" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ALL_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {u.status === "rejected" && (
                      <Button variant="ghost" size="icon" className="text-emerald-600" title="Approve" onClick={() => approve(u)} data-testid={`approve-rejected-${u.email}`}><UserCheck className="h-4 w-4" /></Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => setEdit({ ...u })} data-testid={`edit-user-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                    {u.user_id !== me?.user_id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-red-600" data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This permanently removes the user account.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(u.user_id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit {edit?.name}</DialogTitle>
            <DialogDescription>Update this user's profile details.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>Name</Label><Input value={edit.name || ""} onChange={(e) => setEdit((f) => ({ ...f, name: e.target.value }))} data-testid="edit-user-name" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={edit.phone || ""} onChange={(e) => setEdit((f) => ({ ...f, phone: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Company</Label><Input value={edit.company || ""} onChange={(e) => setEdit((f) => ({ ...f, company: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={edit.city || ""} onChange={(e) => setEdit((f) => ({ ...f, city: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Pincode</Label><Input value={edit.pincode || ""} onChange={(e) => setEdit((f) => ({ ...f, pincode: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="save-user-edit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
